import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface PitchSlip {
  id: string;
  pipeline: string;
  cycleDate: string;
  status: string;
  totalOdds: number | null;
  reasoning: string | null;
  legs: {
    fixture_id: string;
    market: string;
    selection: string;
    odds: number;
    rationale: string | null;
    confidence: number | null;
  }[];
}

export interface UsePitchPicksState {
  slips: PitchSlip[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function usePitchPicks(): UsePitchPicksState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [slips, setSlips] = useState<PitchSlip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setSlips([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const today = new Date().toISOString().slice(0, 10);

    const { data: slipRows, error: slipErr } = await supabase
      .from("daily_slips")
      .select("*")
      .eq("cycle_date", today)
      .in("status", ["published", "sealed"])
      .order("created_at", { ascending: false })
      .limit(5);

    if (slipErr) {
      setSlips([]);
      setError(slipErr.message);
      setLoading(false);
      return;
    }

    if (!slipRows || slipRows.length === 0) {
      setSlips([]);
      setLoading(false);
      return;
    }

    const slipIds = slipRows.map((s) => s.id);

    const { data: legRows, error: legErr } = await supabase
      .from("daily_slip_legs")
      .select("*")
      .in("slip_id", slipIds)
      .order("leg_index", { ascending: true });

    if (legErr) {
      setSlips([]);
      setError(legErr.message);
      setLoading(false);
      return;
    }

    const legsBySlip = new Map<string, typeof legRows>();
    if (legRows) {
      for (const leg of legRows) {
        const existing = legsBySlip.get(leg.slip_id) ?? [];
        existing.push(leg);
        legsBySlip.set(leg.slip_id, existing);
      }
    }

    const mapped: PitchSlip[] = slipRows.map((row) => ({
      id: row.id,
      pipeline: row.pipeline,
      cycleDate: row.cycle_date,
      status: row.status,
      totalOdds: row.total_odds,
      reasoning: row.reasoning,
      legs: (legsBySlip.get(row.id) ?? []).map((l) => ({
        fixture_id: l.fixture_id,
        market: l.market,
        selection: l.selection,
        odds: l.odds,
        rationale: l.rationale,
        confidence: l.surviving_confidence,
      })),
    }));

    setSlips(mapped);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UsePitchPicksState>(
    () => ({ slips, loading, error, refetch }),
    [slips, loading, error, refetch],
  );
}
